import userStateService from "@/common/userState.service.js";

export default {
  data() {
    return {
      tableEl: undefined,
      tableColsEls: undefined,
      columnWidths: {},
      resizeState: null
    };
  },

  beforeDestroy() {
    this.removeResizeListeners();
  },

  methods: {
    columnWidthsStateKey() {
      return `table-state.${this.name}.columnWidths`;
    },

    loadStoredColumnWidths() {
      const saved = userStateService.getState(this.columnWidthsStateKey(), {});

      Object.keys(saved).forEach(id => {
        if (this.columnWidths[id] == null && this.isStoredWidth(saved[id])) {
          this.columnWidths[id] = saved[id];
        }
      });
    },

    persistColumnWidths() {
      const saved = userStateService.getState(this.columnWidthsStateKey(), {});

      userStateService.setState(this.columnWidthsStateKey(), {
        ...saved,
        ...this.columnWidths
      });
    },

    resetColumnWidths() {
      const saved = userStateService.getState(this.columnWidthsStateKey(), {});
      const hasStoredWidths = Object.keys(saved).some(id => this.isStoredWidth(saved[id]));
      const hasMemoryWidths = Object.keys(this.columnWidths).some(id => this.isStoredWidth(this.columnWidths[id]));

      if (!hasStoredWidths && !hasMemoryWidths) {
        return;
      }

      this.columnWidths = {};
      this.resizeState = null;
      userStateService.setState(this.columnWidthsStateKey(), {});
      this.rerenderTable();
    },

    isStoredWidth(value) {
      return typeof value === 'number' && isFinite(value) && value > 0;
    },

    initResizableColumns(saveCurrentColumnWidths = false) {
      this.loadStoredColumnWidths();

      this.tableEl = this.$refs.table.$el.querySelectorAll('table')[0];

      if (!this.tableEl) {
        return;
      }

      let header = this.tableEl.getElementsByTagName('tr')[0];

      this.tableColsEls = header.children;

      this.prepareColumns(header.offsetHeight);

      if (!saveCurrentColumnWidths) {
        for (let i = 0; i < this.tableColsEls.length; i++) {
          this.columnWidths[this.tableColsEls[i].id] = this.tableColsEls[i].offsetWidth;
        }
      }
    },

    prepareColumns(headerHeight) {
      for (let i = 0; i < this.tableColsEls.length; i++) {
        const column = this.tableColsEls[i];

        column.style.position = 'relative';
        column.id = this.localHeaders[i].value;

        if (column.querySelector(':scope > .column-resize-handle')) {
          continue;
        }

        let div = this.createResizeDiv(headerHeight);

        column.appendChild(div);
        this.setListeners(div);
      }

      this.bindResizeListeners();
    },

    createResizeDiv(headerHeight) {
      let div = document.createElement('div');

      div.className = 'column-resize-handle';
      div.style.top = 0;
      div.style.right = 0;
      div.style.width = '5px';
      div.style.position = 'absolute';
      div.style.cursor = 'col-resize';
      div.style.userSelect = 'none';
      div.style.height = headerHeight + 'px';
      div.style.zIndex = 3;

      return div;
    },

    bindResizeListeners() {
      if (this._columnResizeMove) {
        return;
      }

      this._columnResizeMove = event => {
        this.handleColumnResizeMove(event);
      };

      this._columnResizeEnd = () => {
        this.handleColumnResizeEnd();
      };

      document.addEventListener('mousemove', this._columnResizeMove);
      document.addEventListener('mouseup', this._columnResizeEnd);
    },

    removeResizeListeners() {
      if (!this._columnResizeMove) {
        return;
      }

      document.removeEventListener('mousemove', this._columnResizeMove);
      document.removeEventListener('mouseup', this._columnResizeEnd);
      this._columnResizeMove = null;
      this._columnResizeEnd = null;
    },

    setListeners(div) {
      div.addEventListener('click', event => {
        event.stopPropagation();
      });

      div.addEventListener('mousedown', event => {
        event.preventDefault();
        event.stopPropagation();

        const curCol = event.currentTarget.parentElement;
        const index = Array.prototype.indexOf.call(this.tableColsEls, curCol);
        const widths = this.lockAllColumns(true);

        this.resizeState = {
          index,
          pageX: event.pageX,
          startWidth: widths[index]
        };

        event.currentTarget.style.borderRight = '2px solid #e0e0e0';
        event.currentTarget.style.height = this.tableEl.offsetHeight + 'px';
      });

      div.addEventListener('mouseover', event => {
        event.target.style.borderRight = '2px solid #e0e0e0';
        event.target.style.height = this.tableEl.offsetHeight + 'px';
      });

      div.addEventListener('mouseout', event => {
        if (this.resizeState) {
          return;
        }

        event.target.style.borderRight = '';
      });
    },

    handleColumnResizeMove(event) {
      if (!this.resizeState) {
        return;
      }

      event.preventDefault();

      const { index, pageX, startWidth } = this.resizeState;
      const newWidth = Math.max(30, startWidth + (event.pageX - pageX));
      const column = this.tableColsEls[index];

      this.columnWidths[column.id] = newWidth;
      this.applyColumnWidth(index, newWidth);
      this.setTableWidth(this.sumColumnWidths());
    },

    handleColumnResizeEnd() {
      if (!this.resizeState) {
        return;
      }

      const { index, startWidth } = this.resizeState;
      const column = this.tableColsEls[index];
      const widthChanged = column && this.columnWidths[column.id] !== startWidth;

      this.resizeState = null;

      this.tableEl.querySelectorAll('.column-resize-handle').forEach(handle => {
        handle.style.borderRight = '';
      });

      if (widthChanged) {
        this.persistColumnWidths();
      }
    },

    lockAllColumns(measureRendered) {
      const widths = [];

      for (let i = 0; i < this.tableColsEls.length; i++) {
        const column = this.tableColsEls[i];
        const width = measureRendered
          ? column.offsetWidth
          : this.columnWidths[column.id];

        widths.push(width);
        this.columnWidths[column.id] = width;
      }

      this.tableEl.style.tableLayout = 'fixed';

      for (let i = 0; i < widths.length; i++) {
        this.applyColumnWidth(i, widths[i]);
      }

      this.setTableWidth(widths.reduce((sum, width) => sum + width, 0));

      return widths;
    },

    setTableWidth(width) {
      const px = width + 'px';

      this.tableEl.style.width = px;
      this.tableEl.style.minWidth = px;
    },

    applyColumnWidth(index, width) {
      const px = width + 'px';
      const column = this.tableColsEls[index];
      const col = this.tableEl.querySelectorAll('colgroup col')[index];

      this.setElementWidth(column, px);

      if (col) {
        col.style.width = px;
      }
    },

    setElementWidth(element, px) {
      element.style.boxSizing = 'border-box';
      element.style.width = px;
      element.style.minWidth = px;
      element.style.maxWidth = px;
    },

    sumColumnWidths() {
      let total = 0;

      for (let i = 0; i < this.tableColsEls.length; i++) {
        total += this.columnWidths[this.tableColsEls[i].id] || 0;
      }

      return total;
    },

    updateColumnsSize() {
      if (!this.tableEl || !this.tableColsEls || !this.tableColsEls.length) {
        return;
      }

      const hasSavedWidths = Array.prototype.some.call(
        this.tableColsEls,
        column => this.isStoredWidth(this.columnWidths[column.id])
      );

      if (!hasSavedWidths) {
        return;
      }

      for (let i = 0; i < this.tableColsEls.length; i++) {
        const column = this.tableColsEls[i];

        if (!this.isStoredWidth(this.columnWidths[column.id])) {
          this.columnWidths[column.id] = column.offsetWidth;
        }
      }

      this.lockAllColumns(false);
    }
  }
};
